import React, { useState } from 'react';
import { X, AlertCircle, RefreshCcw } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { apiPostJson } from '../../utils/api';
import {
  useServiceRequest,
  useUpdateServiceRequestStatus,
} from './serviceRequestHooks';
import TriageModal from './TriageModal';
import AssignmentControls from './AssignmentControls';

const STATUS_OPTIONS = [
  { value: 'submitted',   label: 'Submitted' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'completed',   label: 'Completed' },
  { value: 'cancelled',   label: 'Cancelled' },
];

export default function ServiceRequestInboxDetailPanel({ publicId, onClose }) {
  const { theme } = useTheme();
  const { currentUser } = useAuth();
  const isDark = theme === 'dark';
  const { data, isLoading } = useServiceRequest(publicId);
  const updateStatus = useUpdateServiceRequestStatus(publicId);
  const [triageOpen, setTriageOpen] = useState(false);

  const sr = data?.service_request;

  const handleStatus = async (status) => {
    try {
      await updateStatus.mutateAsync(status);
      toast.success(`Status set to ${status.replace('_', ' ')}.`);
    } catch (e) {
      console.error(e);
      toast.error('Could not update status.');
    }
  };

  const handleRetryWebhook = async () => {
    try {
      await apiPostJson(`/admin/service_requests/${publicId}/retry_webhook`, {});
      toast.success('Webhook retry queued.');
    } catch (e) {
      console.error(e);
      toast.error('Could not queue retry.');
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <aside
        onClick={(e) => e.stopPropagation()}
        className={`relative h-full w-full max-w-xl overflow-y-auto shadow-xl ${isDark ? 'bg-gray-800' : 'bg-white'}`}
      >
        <div className="flex items-start justify-between p-5 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-inherit z-10">
          <div>
            <h3 className="text-lg font-medium font-rasa">Service request</h3>
            {sr && <p className="text-xs text-gray-500 mt-0.5">#{sr.public_id.slice(0, 8)}</p>}
          </div>
          <button onClick={onClose} className="p-1 rounded-md text-gray-500 hover:text-gray-700">
            <X size={18} />
          </button>
        </div>

        {isLoading && <div className="p-6 text-sm text-gray-500">Loading…</div>}
        {sr && (
          <div className="p-5 space-y-5">
            {sr.triage_state === 'pending' && (
              <div className="rounded-md border border-orange-300 bg-orange-50 dark:bg-orange-900/20 dark:border-orange-700 p-3 flex items-start gap-2">
                <AlertCircle size={16} className="text-orange-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm font-rasa font-medium text-orange-700 dark:text-orange-300">
                    This request needs triage
                  </div>
                  <div className={`text-xs font-rasa mt-0.5 ${isDark ? 'text-orange-200/80' : 'text-orange-700/80'}`}>
                    The requester wasn't sure which service to pick. Route it to the right team.
                  </div>
                  <button
                    type="button"
                    onClick={() => setTriageOpen(true)}
                    className="mt-2 inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs bg-orange-500 text-white hover:bg-orange-600 font-rasa"
                  >
                    Triage now
                  </button>
                </div>
              </div>
            )}

            <Section label="Requester" isDark={isDark}>
              <div className="text-sm font-rasa">{sr.requester?.name || sr.requester?.email}</div>
              <div className={`text-xs font-rasa ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{sr.requester?.email}</div>
            </Section>

            <Section label="Services" isDark={isDark}>
              {sr.items?.length > 0 ? (
                <ul className="space-y-1">
                  {sr.items.map((i) => (
                    <li key={i.slug} className="text-sm font-rasa flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                      {i.label}
                      <span className={`text-xs ml-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        ({i.department_slug.replace('_', ' ')} / {i.service_area_slug.replace('_', ' ')})
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className={`text-xs font-rasa ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No services yet — pending triage.</div>
              )}
              {sr.program_types?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {sr.program_types.map((p) => (
                    <span key={p} className="text-xs px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-rasa">
                      {p.replace('_', ' ')}
                    </span>
                  ))}
                </div>
              )}
            </Section>

            <Section label="Contact" isDark={isDark}>
              {sr.contact?.email && <div className="text-sm font-rasa">{sr.contact.email}</div>}
              {sr.contact?.phone && <div className="text-sm font-rasa">{sr.contact.phone}</div>}
              {sr.company && <div className="text-sm font-rasa">{sr.company}</div>}
              {formatAddress(sr) && <div className="text-sm font-rasa">{formatAddress(sr)}</div>}
              {sr.contact?.preferred_method && (
                <div className={`text-xs font-rasa mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Prefers {sr.contact.preferred_method}
                </div>
              )}
            </Section>

            {sr.notes && (
              <Section label="Notes" isDark={isDark}>
                <p className="text-sm font-rasa whitespace-pre-wrap">{sr.notes}</p>
              </Section>
            )}

            <Section label="Status" isDark={isDark}>
              <div className="flex flex-wrap gap-2">
                {STATUS_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handleStatus(opt.value)}
                    disabled={updateStatus.isPending || sr.status === opt.value}
                    className={`text-xs font-rasa px-3 py-1 rounded-full border ${
                      sr.status === opt.value
                        ? 'bg-blue-500 text-white border-blue-500'
                        : isDark
                          ? 'border-gray-700 text-gray-300 hover:border-gray-500'
                          : 'border-gray-300 text-gray-700 hover:border-gray-400'
                    } disabled:opacity-50`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </Section>

            <Section label="Assignment" isDark={isDark}>
              <AssignmentControls
                publicId={publicId}
                currentAssigneeName={sr.assigned_to}
              />
            </Section>

            {currentUser?.admin && (
              <Section label="Webhook" isDark={isDark}>
                <div className={`text-xs font-rasa ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Attempts: {sr.webhook?.attempts ?? 0}
                  {sr.webhook?.delivered_at && ` · last delivered ${new Date(sr.webhook.delivered_at).toLocaleString()}`}
                </div>
                {sr.webhook?.last_error && (
                  <div className="text-xs font-rasa text-red-500 mt-1 break-words">{sr.webhook.last_error}</div>
                )}
                <button
                  type="button"
                  onClick={handleRetryWebhook}
                  className="mt-2 inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-blue-500 text-white hover:bg-blue-600 font-rasa"
                >
                  <RefreshCcw size={12} /> Retry webhook
                </button>
              </Section>
            )}
          </div>
        )}
      </aside>

      {triageOpen && (
        <TriageModal
          publicId={publicId}
          onClose={() => setTriageOpen(false)}
          onTriaged={() => {}}
        />
      )}
    </div>
  );
}

function Section({ label, children, isDark }) {
  return (
    <div>
      <div className={`text-xs uppercase tracking-wide font-rasa mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{label}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function formatAddress(sr) {
  if (!sr) return '';
  const line2 = [sr.city, sr.state].filter(Boolean).join(', ');
  return [sr.address, line2, sr.zip_code].filter(Boolean).join(' ').trim();
}
